/**
 * Pure helpers for the Employees table client-side row filtering.
 *
 * Extracted from EmployeesOrganizationTable.vue so the filter/flag decision is
 * unit-testable in isolation (the SFC computed depends on stores, composables and
 * LaunchDarkly, which makes it impractical to cover via a full component mount).
 */

/**
 * Legacy client-side filtering that mirrors the SearchModal chips
 * (status / archived / access / assignment). Pure: reads only employee fields.
 */
export const applyEmployeeFilterLogic = (employeeList, filters) => {
  const list = Array.isArray(employeeList) ? employeeList : [];
  if (!filters || filters.length === 0) return list;

  return list.filter((emp) => {
    // Extract filter values (handle both string and object formats)
    const filterValues = filters.map((f) => (typeof f === "object" ? f.value : f));

    // Access filters
    const noAccessFilter = filterValues.find((f) => f === "no_access" || f === "No Access");
    const hasAccessFilter = filterValues.find((f) => f === "has_access" || f === "Has Access");

    if (noAccessFilter) {
      // No access: when access object is null or has_access is false
      if (emp.access !== null && emp.access?.has_access !== false) return false;
    }

    if (hasAccessFilter) {
      // Has access: when access object exists and has_access is true
      if (!emp.access || !emp.access.has_access) return false;
    }

    // Assignment filters
    const hasAssignmentFilter = filterValues.find(
      (f) => f === "has_assignment" || f === "Has Assignments"
    );
    const noAssignmentFilter = filterValues.find(
      (f) => f === "no_assignment" || f === "No Assignments"
    );

    if (hasAssignmentFilter) {
      // Has assignment: employee has primary_position OR preparing_for_positions
      const hasPrimary = emp.primary_position !== null;
      const hasPreparing = emp.preparing_for_positions && emp.preparing_for_positions.length > 0;
      if (!hasPrimary && !hasPreparing) return false;
    }

    if (noAssignmentFilter) {
      // No assignment: employee has no primary_position AND no preparing_for_positions
      const hasPrimary = emp.primary_position !== null;
      const hasPreparing = emp.preparing_for_positions && emp.preparing_for_positions.length > 0;
      if (hasPrimary || hasPreparing) return false;
    }

    // Archived filters (ER-11658: stable values only — no label-string fallbacks)
    const showArchivedFilter = filterValues.find((f) => f === "show_archived");
    const noArchivedFilter = filterValues.find((f) => f === "no_archived");
    const statusFilters = filterValues.filter(
      (f) => f === "active" || f === "terminated" || f === "draft"
    );
    const isStatusFilter = statusFilters.length > 0;
    const isShowArchived = !!showArchivedFilter;
    const isNoArchived = !!noArchivedFilter;

    // (1) Both status and show_archived: show all with status (archived and not)
    if (isStatusFilter && isShowArchived) {
      if (!statusFilters.includes(emp.status)) return false;
      return true;
    }
    // (2) Status only: only non-archived with status
    if (isStatusFilter && !isShowArchived) {
      if (emp.archived === true) return false;
      if (!statusFilters.includes(emp.status)) return false;
      return true;
    }
    // (3) Only show_archived (no status): only archived
    if (!isStatusFilter && isShowArchived) {
      if (emp.archived !== true) return false;
      return true;
    }
    // (4) Only no_archived (no status): only non-archived
    if (!isStatusFilter && isNoArchived) {
      if (emp.archived === true) return false;
      return true;
    }
    // (5) No filters or unknown: return all
    return true;
  });
};

/**
 * Rows to display in the table.
 *
 * MDP-440 (`ideaMdp440Enabled`): the FilterEmployeeModal applies status / archived /
 * department entirely on the API (archived is an `archived OR status` union, department
 * via query params), so we trust the server result as-is and skip the legacy client-side
 * re-filter — which would otherwise drop backend-matched rows (e.g. archived employees
 * whose status isn't in the selected set). Flag off preserves the legacy behavior.
 */
export const selectVisibleEmployees = (employeeList, filterValues, ideaMdp440Enabled) => {
  const list = Array.isArray(employeeList) ? employeeList : [];
  if (ideaMdp440Enabled) return list;
  if (Array.isArray(filterValues) && filterValues.length > 0) {
    return applyEmployeeFilterLogic(list, filterValues);
  }
  return list;
};

/**
 * True when the applied filters are empty or exactly the list's default set (any order).
 * Defaults are always applied on mount, so "no filters" alone never holds for a fresh list.
 */
export const isDefaultOrEmptyFilterSet = (values, defaults) => {
  const toValue = (f) => (f && typeof f === "object" ? f.value : f);
  const applied = (Array.isArray(values) ? values : []).map(toValue).filter(Boolean);
  if (applied.length === 0) return true;
  const defaultValues = (Array.isArray(defaults) ? defaults : []).map(toValue).filter(Boolean);
  return (
    applied.length === defaultValues.length && applied.every((v) => defaultValues.includes(v))
  );
};

// ER-10905: the default Archived value never renders a chip; compared by value, not label.
export const omitArchivedDefaultValue = (values) =>
  (Array.isArray(values) ? values : []).filter((value) => value !== "no_archived");
